import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

// UI

// Components
import FullTable, { tableInitialState } from '@/components/Table/FullTable/FullTable'

// Hooks

// Constants

// Helpers
import { createColumnHelper } from '@tanstack/react-table'

// Slices

// Components
import { MySourcingOfferDisplayLevel } from '@/features/tableProfiles/constants/tableProfile.constant'
import { authSelector } from '@/stores/auth/authSlice'
import { convertTableStateToQuery } from '@/utils/tableHelper'
import { Box, Flex, Spinner } from '@chakra-ui/react'
import useOfferList from '../../hooks/useOfferList'

import {
  getMySourcingTableColumns,
  mySourcingCostBudgetRfqColumnAccessors,
  mySourcingPRColumnAccessors,
  mySourcingPRLineItemColumnAccessors,
} from './mySourcingTableColumns'
import MySourcingPageHeader from './MySourcingPageHeader'
const MySourcingTable = ({ userTableProfile, URLSelectedProfileId = '', columnData = {} }) => {
  const initialTableState = useMemo(() => {
    try {
      if (!userTableProfile) {
        throw new Error('1')
      }

      if (URLSelectedProfileId !== '') {
        const selectedProfile = userTableProfile.profiles.find((profile) => profile._id === URLSelectedProfileId)

        if (!selectedProfile?.tableDetails) {
          throw new Error('2')
        }
        return selectedProfile.tableDetails
      }

      if (!userTableProfile?.defaultProfile?.tableDetails) {
        throw new Error('3')
      }
      return userTableProfile?.defaultProfile?.tableDetails
    } catch (err) {
      console.log(err)
      return tableInitialState
    }
  }, [URLSelectedProfileId, userTableProfile, tableInitialState])

  const initialTableDisplayLevel = useMemo(() => {
    try {
      if (!userTableProfile) {
        throw new Error('1')
      }
      if (URLSelectedProfileId !== '') {
        const selectedProfile = userTableProfile.profiles.find((profile) => profile._id === URLSelectedProfileId)

        if (!selectedProfile?.tableDetails) {
          throw new Error('2')
        }
        return selectedProfile.displayLevel
      }

      if (!userTableProfile?.defaultProfile?.tableDetails) {
        throw new Error('3')
      }
      return userTableProfile?.defaultProfile?.displayLevel
    } catch (err) {
      console.log(err)
      return MySourcingOfferDisplayLevel.COST_BUDGET
    }
  }, [URLSelectedProfileId, userTableProfile, tableInitialState])

  const [tableState, setTableState] = useState(initialTableState)
  const [displayLevel, setDisplayLevel] = useState(initialTableDisplayLevel)

  const query = convertTableStateToQuery(tableState)
  const columnHelper = createColumnHelper()
  const { user: currentUser } = useSelector(authSelector)

  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { useFetchMySourcingOffers } = useOfferList()

  const {
    data: offersResponse,
    isLoading: isLoadingOffer,
    isRefetching,
  } = useFetchMySourcingOffers(query, displayLevel)

  const allColumns = useMemo(() => {
    const defaultColumns = getMySourcingTableColumns(columnData, displayLevel)
    return defaultColumns
  }, [columnHelper, columnData, displayLevel])

  const handleGotoOffer = useCallback(
    (row) => {
      if (!row?.original?._id) {
        return
      }
      window.open(`${window.location.origin}/offers/${row?.original?._id}`)
    },
    [navigate]
  )

  useEffect(() => {
    switch (displayLevel) {
      case MySourcingOfferDisplayLevel.COST_BUDGET: {
        const accessorsToHide = [
          ...mySourcingCostBudgetRfqColumnAccessors,
          ...mySourcingPRColumnAccessors,
          ...mySourcingPRLineItemColumnAccessors,
        ].reduce((acc, key) => {
          acc[key] = false
          return acc
        }, {})
        setTableState((prev) => ({
          ...prev,
          columnVisibility: { ...prev.columnVisibility, ...accessorsToHide },
        }))
        break
      }
      case MySourcingOfferDisplayLevel.COST_BUDGET_RFQ: {
        const accessorsToHide = [...mySourcingPRColumnAccessors, ...mySourcingPRLineItemColumnAccessors].reduce(
          (acc, key) => {
            acc[key] = false
            return acc
          },
          {}
        )
        setTableState((prev) => ({
          ...prev,
          columnVisibility: { ...prev.columnVisibility, ...accessorsToHide },
        }))
        break
      }
      case MySourcingOfferDisplayLevel.PR: {
        const accessorsToHide = [
          ...mySourcingCostBudgetRfqColumnAccessors,
          ...mySourcingPRLineItemColumnAccessors,
        ].reduce((acc, key) => {
          acc[key] = false
          return acc
        }, {})
        setTableState((prev) => ({
          ...prev,
          columnVisibility: { ...prev.columnVisibility, ...accessorsToHide },
        }))
        break
      }
      case MySourcingOfferDisplayLevel.PR_LINE_ITEM: {
        const accessorsToHide = [...mySourcingCostBudgetRfqColumnAccessors].reduce((acc, key) => {
          acc[key] = false
          return acc
        }, {})
        setTableState((prev) => ({
          ...prev,
          columnVisibility: { ...prev.columnVisibility, ...accessorsToHide },
        }))
        break
      }
    }
  }, [displayLevel])

  if (isLoadingOffer) {
    return (
      <Flex justifyContent="center" alignItems="center" height="100vh">
        <Spinner size="xl" />
      </Flex>
    )
  }
  return (
    <>
      <Flex direction={'column'} gap="2">
        <MySourcingPageHeader
          count={offersResponse?.count ?? 0}
          displayLevel={displayLevel}
          query={query}
          tableState={tableState}
          userTableProfile={userTableProfile}
          setTableState={setTableState}
          setTableDisplayLevel={setDisplayLevel}
          URLSelectedProfileId={URLSelectedProfileId}
        />
      </Flex>
      <Box py={5} w="full" h="90%" overflowY="scroll">
        <FullTable
          data={offersResponse?.data ?? []}
          rowCount={offersResponse?.count ?? 0}
          columnDef={allColumns}
          state={tableState}
          setState={setTableState}
          handleRowClick={handleGotoOffer}
          showColumnGroup={true}
          displayLevel={displayLevel}
          isLoading={isRefetching}
        />
      </Box>
    </>
  )
}

export default MySourcingTable
